import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { isSuperAdminOrOwner, staffRoleLabel } from '../../lib/staffRoles';
import PlatformLayout from '../../components/layout/PlatformLayout';
import './Platform.css';
import { Users, Plus, Trash2, X, AlertCircle } from 'lucide-react';
import PasswordInput from '../../components/PasswordInput';

export default function PlatformStaff() {
  const { platformAxios, platformUser } = usePlatformAuth();
  const queryClient = useQueryClient();

  const [showAddModal, setShowAddModal] = useState(false);
  const [formError, setFormError] = useState('');

  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'admin'
  });

  const { data: staff, isLoading } = useQuery({
    queryKey: ['platform-staff'],
    queryFn: () => platformAxios.get('/staff').then(res => res.data),
    enabled: isSuperAdminOrOwner(platformUser?.role)
  });

  const addStaffMutation = useMutation({
    mutationFn: (data) => platformAxios.post('/staff', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-staff'] });
      setShowAddModal(false);
      setExistingAccount(null);
      setUnconfirmed(null);
      setForm({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'admin'
      });
      setFormError('');
    },
    onError: (err, vars) => {
      // B-70: an account whose email isn't confirmed can't be made staff
      // (either step). Say so plainly, about that address, and drop any
      // "Make them staff?" prompt.
      if (err.response?.status === 409 && err.response?.data?.code === 'unconfirmed_email') {
        setExistingAccount(null);
        setFormError('');
        setUnconfirmed({ email: vars?.email || form.email, message: err.response.data.error });
        return;
      }
      // 8 Oct: an email that already has a Marakin account → offer to make
      // that account staff (it keeps its own password).
      const ex = err.response?.status === 409 && err.response?.data?.existingAccount;
      // Remember WHICH email/role the prompt is about, so editing the form
      // afterwards can never promote a different account.
      if (ex) { setExistingAccount({ ...ex, email: form.email, role: form.role }); setFormError(''); return; }
      setFormError(err.response?.data?.error || 'Failed to add staff member');
    }
  });

  const [existingAccount, setExistingAccount] = useState(null);
  const [unconfirmed, setUnconfirmed] = useState(null); // B-70: { email, message }
  const promoteExisting = () => {
    setFormError('');
    setUnconfirmed(null);
    addStaffMutation.mutate({ email: existingAccount.email, role: existingAccount.role, promoteExisting: true });
  };

  const deactivateStaffMutation = useMutation({
    mutationFn: (id) => platformAxios.delete(`/staff/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-staff'] });
    },
    onError: (err) => {
      setFormError(err.response?.data?.error || 'Failed to deactivate staff member');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    setExistingAccount(null);
    setUnconfirmed(null);

    if (!form.email || !form.password || !form.firstName || !form.lastName) {
      setFormError('All fields are required');
      return;
    }
    if (form.password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }

    addStaffMutation.mutate(form);
  };

  const handleDeactivate = (id) => {
    if (confirm('Are you sure you want to deactivate this staff member?')) {
      deactivateStaffMutation.mutate(id);
    }
  };

  if (!isSuperAdminOrOwner(platformUser?.role)) {
    return (
      <PlatformLayout>
        <div className="platform-page">
          <div className="error-box">
            <AlertCircle size={24} />
            <p>You don't have permission to view this page</p>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout>
      <div className="platform-page">
        <div className="page-header">
          <h1>Staff</h1>
          <p>Manage Marakin platform staff members</p>
        </div>

        <div className="section-toolbar">
          <button
            className="btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={18} />
            Add Staff Member
          </button>
        </div>

        {isLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading staff...</p>
          </div>
        ) : staff && staff.length > 0 ? (
          <div className="staff-table-container">
            <table className="staff-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {staff.map((member) => (
                  <tr key={member.id} className="table-row">
                    <td className="staff-name-cell">
                      <div className="staff-avatar">
                        {member.first_name?.[0]}{member.last_name?.[0]}
                      </div>
                      <div>
                        <strong>
                          {member.first_name} {member.last_name}
                        </strong>
                        <span style={{ marginLeft: 8, padding: '1px 7px', borderRadius: 100, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', background: '#1E4A35', color: '#F4EFE6', whiteSpace: 'nowrap' }}>
                          ★ Marakin Staff
                        </span>
                      </div>
                    </td>
                    <td className="text-muted">{member.email}</td>
                    <td>
                      <span className="role-badge">
                        {staffRoleLabel(member.role)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${member.is_active ? 'active' : 'inactive'}`}>
                        {member.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-muted">
                      {new Date(member.created_at).toLocaleDateString()}
                    </td>
                    <td className="text-center">
                      {/* v53: nobody removes the Owner (the server refuses too) */}
                      {member.id !== platformUser?.id && member.role !== 'owner' && (
                        <button
                          className="btn-icon-danger"
                          onClick={() => handleDeactivate(member.id)}
                          disabled={deactivateStaffMutation.isPending}
                          title="Deactivate"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <Users size={48} />
            <h3>No staff members</h3>
            <p>Add your first staff member</p>
            <button
              className="btn-primary"
              onClick={() => setShowAddModal(true)}
            >
              Add Staff Member
            </button>
          </div>
        )}

        {/* Add Staff Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={() => { setShowAddModal(false); setExistingAccount(null); setUnconfirmed(null); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add Staff Member</h2>
                <button
                  className="btn-close"
                  onClick={() => { setShowAddModal(false); setExistingAccount(null); setUnconfirmed(null); }}
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="modal-form">
                {formError && (
                  <div className="error-box">
                    <AlertCircle size={18} />
                    <p>{formError}</p>
                  </div>
                )}

                {/* B-70: the server won't make an unconfirmed account staff */}
                {unconfirmed && (
                  <div className="error-box" role="alert" data-testid="staff-unconfirmed">
                    <AlertCircle size={18} />
                    <div>
                      <p><strong>{unconfirmed.email}</strong> can't be made staff yet.</p>
                      <p style={{ marginTop: 6 }}>{unconfirmed.message}</p>
                    </div>
                  </div>
                )}

                {existingAccount && (
                  <div className="error-box" role="alert">
                    <AlertCircle size={18} />
                    <div>
                      <p>
                        <strong>{existingAccount.email}</strong> already belongs to{' '}
                        <strong>{[existingAccount.firstName, existingAccount.lastName].filter(Boolean).join(' ') || 'an existing member'}</strong>.
                        Make them {existingAccount.role === 'super_admin' ? 'a super admin' : 'staff'}? They keep their own password; the name and password above are ignored.
                      </p>
                      <p style={{ marginTop: 6 }}>
                        As staff they become an admin of <strong>every</strong> club, can sign into this portal, and stop appearing in club member searches.
                      </p>
                      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <button type="button" className="btn btn-primary btn-sm" onClick={promoteExisting} disabled={addStaffMutation.isPending}>
                          Make them staff
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExistingAccount(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      placeholder="John"
                      value={form.firstName}
                      onChange={(e) =>
                        setForm(prev => ({ ...prev, firstName: e.target.value }))
                      }
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      placeholder="Doe"
                      value={form.lastName}
                      onChange={(e) =>
                        setForm(prev => ({ ...prev, lastName: e.target.value }))
                      }
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    placeholder="john@marakin.co.uk"
                    value={form.email}
                    onChange={(e) =>
                      { setForm(prev => ({ ...prev, email: e.target.value })); setExistingAccount(null); setUnconfirmed(null); }
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password *</label>
                  <PasswordInput
                    className=""
                    placeholder="••••••••"
                    value={form.password}
                    onChange={(e) =>
                      setForm(prev => ({ ...prev, password: e.target.value }))
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={form.role}
                    onChange={(e) =>
                      { setForm(prev => ({ ...prev, role: e.target.value })); setExistingAccount(null); }
                    }
                  >
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => { setShowAddModal(false); setExistingAccount(null); setUnconfirmed(null); }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={addStaffMutation.isPending}
                  >
                    {addStaffMutation.isPending ? 'Adding...' : 'Add Staff Member'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
